/**
 * Data layer for the COMPANY panel's Guided Path section.
 *
 * NO PLAN TEXT REACHES THIS FILE, because the backend never sends any. A plan's
 * name is the first fifteen characters of the prompt the person typed; its
 * description, daily expectation and rituals are a model writing about them by
 * name; and the questionnaire behind a custom plan is them saying what they are
 * struggling with and why. None of it is aggregated, none of it is returned,
 * and there is no per-person fetcher here because there is no per-person
 * endpoint to call.
 *
 * THERE IS NO COMPLETION RATE, and nothing in this file should add one.
 * `GuidedPath.isCompleted` is set by a cron on any expired custom plan whether
 * the person finished it or never opened it, and is skipped entirely for AI
 * plans — so the same field means two different things depending on the kind of
 * plan, and a rate built from it would be a chart of the calendar. What arrives
 * instead is `followThrough` (rituals ticked as a share of rituals offered) and
 * `daysEngaged`, both derived from the day records and both meaning the same
 * thing for either kind.
 *
 * TWO COUNTS THAT DO NOT RECONCILE, ON PURPOSE. `daysFinished` counts days
 * flagged complete; `daysEngaged` counts days with at least one ritual actually
 * ticked. The app can set the first without the second — "complete all for
 * today" flips the day flag without touching the rituals — so either can exceed
 * the other. They are shown side by side and never subtracted or divided.
 *
 * SUPPRESSION HAPPENS AT TWO LEVELS. The whole section returns
 * `{ suppressed: true, minimum }` under the backend's minimum, and separately a
 * single kind of plan with too few people is withheld from the overview's
 * comparison and returns its own suppressed payload on its own tab. Pages must
 * check `suppressed` before reading anything else, and `error` before that —
 * a failed request is not a small cohort.
 *
 * Backed by the company/v1 guided-path-hub endpoints (token-scoped to the
 * company; the B2B/Members toggle is appended by api/companyApi.js). Every
 * fetcher takes an optional `department` so the section's filter narrows the
 * whole page.
 */

import {
  getCompanyGuidedPathDashboard,
  getCompanyGuidedPathPlanType,
  getCompanyGuidedPathUsers
} from '../../../../api/companyApi';
import { unwrap } from '../../../SleepHub/sleepHubApi';

/**
 * What a failed request becomes.
 *
 * `GetApi` swallows network errors and returns undefined, and `unwrap` then
 * throws — so without this every fetcher rejects and the page dies in an
 * uncaught-error overlay the moment the API is unreachable. "Failed" must stay
 * distinguishable from "suppressed" and from "nobody has started a plan".
 */
const failedShape = () => ({ error: true, suppressed: false });

const suppressedShape = (data) => ({
  suppressed: true,
  minimum: data.minimum ?? 0,
  totalUsers: data.totalUsers ?? 0,
  key: data.key ?? null,
  label: data.label ?? null
});

const list = (rows) => (Array.isArray(rows) ? rows : []);

/** The shared engagement block, present on the overview and on both tabs. */
const activityFields = (data) => ({
  dayRecords: data.dayRecords ?? 0,
  ritualsOffered: data.ritualsOffered ?? 0,
  ritualsTicked: data.ritualsTicked ?? 0,
  daysFinished: data.daysFinished ?? 0,
  daysEngaged: data.daysEngaged ?? 0,
  engagedPeople: data.engagedPeople ?? 0,
  followThrough: data.followThrough ?? 0,
  dayTakeUp: data.dayTakeUp ?? 0,
  ritualsPerActiveDay: data.ritualsPerActiveDay ?? null,
  activeDaySplit: list(data.activeDaySplit),
  weekday: list(data.weekday),
  dailyTrend: list(data.dailyTrend),
  returning: data.returning || {
    activeLastWeek: 0,
    activeWeekBefore: 0,
    returned: 0,
    returnRate: 0
  }
});

/** The shared plan block. */
const planFields = (data) => ({
  plans: data.plans ?? 0,
  running: data.running ?? 0,
  ended: data.ended ?? 0,
  tenureSplit: list(data.tenureSplit),
  minuteSplit: list(data.minuteSplit),
  startedTrend: list(data.startedTrend)
});

const adaptTypeRow = (row) => ({
  key: row.key,
  label: row.label,
  blurb: row.blurb || '',
  suppressed: !!row.suppressed,
  people: row.people ?? 0,
  takeUp: row.takeUp ?? 0,
  plans: row.plans ?? 0,
  running: row.running ?? 0,
  daysEngaged: row.daysEngaged ?? 0,
  ritualsTicked: row.ritualsTicked ?? 0,
  followThrough: row.followThrough ?? 0
});

const adaptOverview = (data) => {
  if (data?.suppressed) return suppressedShape(data);
  return {
    suppressed: false,
    minimum: data.minimum ?? 0,
    totalUsers: data.totalUsers ?? 0,

    // Reach is de-duplicated across both kinds, so somebody holding an AI plan
    // and a custom plan is one person who took up the feature, not two.
    people: data.people ?? 0,
    takeUp: data.takeUp ?? 0,

    ...planFields(data),
    ...activityFields(data),

    types: list(data.types).map(adaptTypeRow),
    // Summed over reported kinds only; `totalsAreComplete` says whether a kind
    // is missing from them.
    reportedPeople: data.reportedPeople ?? 0,
    reportedPlans: data.reportedPlans ?? 0,
    totalsAreComplete: data.totalsAreComplete !== false,
    // Withheld outright when either kind is too small — with only two kinds, a
    // spread plus a partial total gives the hidden one away.
    breadthSplit: list(data.breadthSplit)
  };
};

const adaptPlanType = (data) => {
  if (data?.suppressed) return suppressedShape(data);
  return {
    suppressed: false,
    minimum: data.minimum ?? 0,
    key: data.key,
    label: data.label,
    blurb: data.blurb || '',
    totalUsers: data.totalUsers ?? 0,
    people: data.people ?? 0,
    takeUp: data.takeUp ?? 0,
    plansPerPerson: data.plansPerPerson ?? null,
    undated: data.undated ?? 0,
    ...planFields(data),
    ...activityFields(data)
  };
};

export const fetchGuidedPathOverview = (department = '') =>
  getCompanyGuidedPathDashboard(department)
    .then((res) => adaptOverview(unwrap(res) || {}))
    .catch(() => failedShape());

export const fetchGuidedPathPlanType = (planType, department = '') =>
  getCompanyGuidedPathPlanType(planType, department)
    .then((res) => adaptPlanType(unwrap(res) || {}))
    .catch(() => failedShape());

export const fetchGuidedPathUsers = ({ page, perPage, search, department }) =>
  getCompanyGuidedPathUsers({ page, perPage, search, department }).then((res) => {
    const data = unwrap(res) || {};
    return {
      data: list(data.data),
      totalCount: data.totalCount ?? 0,
      planTypes: list(data.planTypes)
    };
  });
